// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
// DemoManagementPage — Admin-only page for managing demo accounts,
// running data scrubs, and viewing deletion receipts.
// Route: /demo-management (admin-only)
// ================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import DemoDataScrubber, { DemoExpiryChecker, generateDeletionReceipt } from '../components/DemoDataScrubber';
import { supabase } from '../supabaseClient';

// ── Styles ──
const styles = {
  page: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '24px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: '#E2E8F0',
  },
  header: {
    marginBottom: 28,
  },
  title: {
    fontSize: 26,
    fontWeight: 800,
    color: '#F8FAFC',
    margin: '0 0 6px 0',
  },
  subtitle: {
    fontSize: 14,
    color: '#94A3B8',
    margin: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    marginBottom: 28,
  },
  statCard: (color) => ({
    background: '#1E293B',
    borderRadius: 12,
    padding: '18px 20px',
    borderLeft: `4px solid ${color}`,
  }),
  statValue: {
    fontSize: 28,
    fontWeight: 800,
    color: '#F8FAFC',
    margin: 0,
  },
  statLabel: {
    fontSize: 12,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 1,
    margin: '4px 0 0 0',
  },
  section: {
    background: '#0F172A',
    borderRadius: 14,
    border: '1px solid #1E293B',
    marginBottom: 20,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #1E293B',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#F8FAFC',
    margin: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '10px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 1,
    background: '#0F172A',
    borderBottom: '1px solid #1E293B',
  },
  td: {
    padding: '12px 16px',
    fontSize: 13,
    color: '#CBD5E1',
    borderBottom: '1px solid #0F172A',
  },
  badge: (type) => {
    const colors = {
      active: { bg: '#064E3B', color: '#34D399', border: '#059669' },
      expired: { bg: '#450A0A', color: '#F87171', border: '#DC2626' },
      scrubbed: { bg: '#1E293B', color: '#94A3B8', border: '#475569' },
      converted: { bg: '#1E3A5F', color: '#38BDF8', border: '#0284C7' },
    };
    const c = colors[type] || colors.active;
    return {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 700,
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    };
  },
  btn: (variant = 'primary') => ({
    padding: variant === 'small' ? '6px 14px' : '10px 20px',
    borderRadius: 8,
    border: variant === 'primary' ? 'none' : '1px solid #334155',
    background: variant === 'primary' ? '#DC2626' : variant === 'success' ? '#059669' : '#1E293B',
    color: '#fff',
    fontSize: variant === 'small' ? 12 : 14,
    fontWeight: 700,
    cursor: 'pointer',
  }),
  emptyState: {
    padding: 40,
    textAlign: 'center',
    color: '#475569',
    fontSize: 14,
  },
};

export default function DemoManagementPage() {
  const { user } = useAuth();
  const [demos, setDemos] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeScrub, setActiveScrub] = useState(null);
  const [tab, setTab] = useState('demos'); // demos | receipts | scrubber

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch demo accounts
      const { data: demoData } = await supabase
        .from('demo_accounts')
        .select('*')
        .order('created_at', { ascending: false });
      setDemos(demoData || []);

      // Fetch deletion receipts
      const { data: receiptData } = await supabase
        .from('deletion_receipts')
        .select('*')
        .order('created_at', { ascending: false });
      setReceipts(receiptData || []);
    } catch (e) {
      console.warn('DemoManagement: fetch error:', e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Stats
  const activeCount = demos.filter(d => d.demo_status === 'active').length;
  const expiredCount = demos.filter(d => {
    if (d.demo_status !== 'active') return false;
    const end = d.demo_end_date || new Date(new Date(d.demo_start_date).getTime() + 7 * 86400000).toISOString();
    return new Date() > new Date(end);
  }).length;
  const scrubbedCount = demos.filter(d => d.demo_status === 'scrubbed').length;
  const convertedCount = demos.filter(d => d.demo_status === 'converted').length;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-CA') : '—';
  const daysUntilExpiry = (d) => {
    const end = d.demo_end_date || new Date(new Date(d.demo_start_date).getTime() + 7 * 86400000).toISOString();
    const diff = Math.ceil((new Date(end) - new Date()) / 86400000);
    return diff;
  };

  const handleScrub = (demo) => {
    setActiveScrub(demo);
    setTab('scrubber');
  };

  const downloadStoredReceipt = (r) => {
    const receiptObj = {
      receiptId: r.receipt_id,
      companyId: r.company_id,
      companyName: r.company_name,
      appName: r.app_name,
      tablesScanned: r.tables_scanned || [],
      recordsDeleted: r.records_deleted || {},
      totalRecordsDeleted: r.total_records_deleted || 0,
      dataCategories: r.data_categories || [],
      scrubberVersion: r.scrubber_version,
      initiatedBy: r.initiated_by,
      completedAt: r.created_at,
    };
    const html = generateDeletionReceipt(receiptObj);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deletion-receipt-${r.receipt_id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabBtn = (name, label) => ({
    padding: '8px 20px',
    borderRadius: 8,
    border: tab === name ? '2px solid #38BDF8' : '1px solid #334155',
    background: tab === name ? '#1E293B' : 'transparent',
    color: tab === name ? '#38BDF8' : '#94A3B8',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    marginRight: 8,
  });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: 60, color: '#64748B' }}>Loading demo data...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Demo Management</h1>
        <p style={styles.subtitle}>
          Manage demo trial accounts, run data scrubs, and download proof-of-deletion receipts
        </p>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard('#38BDF8')}>
          <div style={styles.statValue}>{activeCount}</div>
          <div style={styles.statLabel}>Active Demos</div>
        </div>
        <div style={styles.statCard('#F87171')}>
          <div style={styles.statValue}>{expiredCount}</div>
          <div style={styles.statLabel}>Expired (Need Scrub)</div>
        </div>
        <div style={styles.statCard('#94A3B8')}>
          <div style={styles.statValue}>{scrubbedCount}</div>
          <div style={styles.statLabel}>Scrubbed</div>
        </div>
        <div style={styles.statCard('#34D399')}>
          <div style={styles.statValue}>{convertedCount}</div>
          <div style={styles.statLabel}>Converted</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: 20 }}>
        <button style={tabBtn('demos', 'Demo Accounts')} onClick={() => setTab('demos')}>
          Demo Accounts
        </button>
        <button style={tabBtn('receipts', 'Receipts')} onClick={() => setTab('receipts')}>
          Deletion Receipts ({receipts.length})
        </button>
        {activeScrub && (
          <button style={tabBtn('scrubber', 'Scrubber')} onClick={() => setTab('scrubber')}>
            Active Scrub
          </button>
        )}
      </div>

      {/* Demo Accounts Tab */}
      {tab === 'demos' && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Demo Accounts</h2>
            <button style={styles.btn('secondary')} onClick={fetchData}>Refresh</button>
          </div>
          {demos.length === 0 ? (
            <div style={styles.emptyState}>No demo accounts found. Demos will appear here when businesses start a trial.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Company</th>
                    <th style={styles.th}>Contact</th>
                    <th style={styles.th}>Start</th>
                    <th style={styles.th}>End</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Days Left</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {demos.map((d) => {
                    const days = daysUntilExpiry(d);
                    const isExpired = d.demo_status === 'active' && days <= 0;
                    const statusKey = d.demo_status === 'active' && isExpired ? 'expired' : d.demo_status;
                    return (
                      <tr key={d.id} style={{ background: isExpired ? 'rgba(220,38,38,0.06)' : 'transparent' }}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700, color: '#F1F5F9' }}>{d.company_name || '—'}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontFamily: 'monospace' }}>{d.company_id?.slice(0, 8)}...</div>
                        </td>
                        <td style={styles.td}>
                          <div>{d.contact_email || '—'}</div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>{d.contact_name || ''}</div>
                        </td>
                        <td style={styles.td}>{formatDate(d.demo_start_date)}</td>
                        <td style={styles.td}>{formatDate(d.demo_end_date)}</td>
                        <td style={styles.td}>
                          <span style={styles.badge(statusKey)}>{statusKey}</span>
                        </td>
                        <td style={{ ...styles.td, fontWeight: 700, color: isExpired ? '#F87171' : days <= 2 ? '#FBBF24' : '#34D399' }}>
                          {d.demo_status === 'scrubbed' ? '—' : d.demo_status === 'converted' ? '—' : isExpired ? `${Math.abs(days)}d overdue` : `${days}d`}
                        </td>
                        <td style={styles.td}>
                          {(d.demo_status === 'active' && isExpired) && (
                            <button style={styles.btn('small')} onClick={() => handleScrub(d)}>
                              Scrub Now
                            </button>
                          )}
                          {d.demo_status === 'scrubbed' && (
                            <span style={{ fontSize: 12, color: '#475569' }}>Cleaned</span>
                          )}
                          {d.demo_status === 'converted' && (
                            <span style={{ fontSize: 12, color: '#38BDF8' }}>Paying</span>
                          )}
                          {d.demo_status === 'active' && !isExpired && (
                            <span style={{ fontSize: 12, color: '#64748B' }}>Active</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Deletion Receipts Tab */}
      {tab === 'receipts' && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Deletion Receipts</h2>
          </div>
          {receipts.length === 0 ? (
            <div style={styles.emptyState}>No deletion receipts yet. Receipts are generated when demo data is scrubbed.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Receipt ID</th>
                    <th style={styles.th}>Company</th>
                    <th style={styles.th}>App</th>
                    <th style={styles.th}>Records Deleted</th>
                    <th style={styles.th}>Initiated By</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r) => (
                    <tr key={r.id}>
                      <td style={{ ...styles.td, fontFamily: 'monospace', color: '#38BDF8', fontWeight: 600, letterSpacing: 1 }}>
                        {r.receipt_id}
                      </td>
                      <td style={styles.td}>{r.company_name || '—'}</td>
                      <td style={styles.td}>{r.app_name || '—'}</td>
                      <td style={{ ...styles.td, fontWeight: 700, color: '#F87171' }}>
                        {r.total_records_deleted || 0}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.badge(r.initiated_by === 'auto_expiry' ? 'expired' : 'active')}>
                          {r.initiated_by || 'manual'}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDate(r.created_at)}</td>
                      <td style={styles.td}>
                        <button style={styles.btn('small')} onClick={() => downloadStoredReceipt(r)}>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Active Scrubber Tab */}
      {tab === 'scrubber' && activeScrub && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button style={styles.btn('secondary')} onClick={() => { setActiveScrub(null); setTab('demos'); }}>
              Back to Demo Accounts
            </button>
          </div>
          <DemoDataScrubber
            supabase={supabase}
            companyId={activeScrub.company_id}
            appName={activeScrub.app_name || 'Unknown'}
            onComplete={(receipt) => {
              // Refresh data after scrub
              setTimeout(() => fetchData(), 1000);
            }}
          />
        </div>
      )}
    </div>
  );
}
